/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-25 17:45:06
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Card, Tabs, Stack, Button, PageActions } from '@shopify/polaris';
import { reduxForm, getFormValues, change, reset } from 'redux-form';
import { withI18n } from '@shopify/react-i18n';

import MainMenu from 'src/components/containers/main-menu';

import { URL } from 'src/constants/links';
import DidYouKnow from 'src/components/views/tools/custom-sorting/did-you-know';
import SortByOptions from 'src/components/views/tools/custom-sorting/sort-by-options';
import DefaultSortingOptions from 'src/components/views/tools/custom-sorting/default-sorting-options';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import SendRequestFeatureModal from 'src/components/containers/common/send-request-feature-modal';
import RequestResultModal from 'src/components/containers/common/request-result-modal';
import ProcessingUpdateBanner from 'src/components/views/common/processing-update-banner';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import { confirmLeavePage } from 'src/helpers';
import {
  buildInitialCustomSortingValues,
  buildCustomSortingValues
} from 'src/helpers/custom-sorting-helper';
import * as CustomSortingActions from 'src/stores/actions/custom-sorting-actions';
import * as FilterDataActions from 'actions/filter-data-actions';
import * as quickStartGuideAction from 'actions/dashboard/quick-start-guide-action';

import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import InstallationRemindStatusBanner from 'src/components/containers/common/installation-remind-status-banner';
import ThemeUpgradeFlow2Notification from 'src/components/containers/common/theme-upgrade-flow-2-notification';
import { FEATURE_IDS } from 'src/constants/theme-upgrade';
import TitleHelp from 'src/components/views/common/title-help';

import type { ComposedProps, State } from './index.type';

class CustomSorting extends Component<ComposedProps, State> {
  state = {
    // Tab
    selected: 0,

    // Modal send request result
    activeRequestModal: false,
    activeModalResult: false,

    // Change position
    positionFlag: false,

    activeModalConfirm: false,
    actionToGo: (params: ?Object) => {},
    params: {}
  };

  componentDidMount() {
    const { getAvailableSortTypes, getCustomSorting, getFilterData } = this.props;

    // Get collection data
    getFilterData();

    // Get sort data
    getAvailableSortTypes();
    getCustomSorting();

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps) {
    const { saving, deleting, scheduleSyncIn, waitSyncAt } = nextProps;
    const {
      saving: savingPrev,
      deleting: deletingPrev,
      getSyncProcessStatus,
      updateShowBanner
    } = this.props;

    // Check to show banner Processing update when change status/ import
    if ((savingPrev && !saving) || (deletingPrev && !deleting)) {
      if (waitSyncAt) {
        // Time from now to sync point
        let syncDuration = waitSyncAt - Math.floor(Date.now() / 1000);
        syncDuration = syncDuration > 0 ? syncDuration : 0;
        // Time to finish sync point
        syncDuration = Math.floor(syncDuration / 60) + scheduleSyncIn;

        updateShowBanner(syncDuration, true);
      }
      // Call sync process status
      getSyncProcessStatus();
    }
  }

  render() {
    const {
      i18n,
      pristine,
      submitting,
      handleSubmit,
      dirty,
      loading,
      saving,
      success,
      syncDuration,
      showScheduleSync,
      sortOptions,
      sortTypes,
      defaultSorting,
      enabledFeatureBestSelling,
      enabledFeatureManual,
      formValues,
      enabledFeatureRelevance,
      updateCustomSortingForm,
      tenantApp,
      history
    } = this.props;

    const { selected, activeRequestModal, activeModalResult } = this.state;

    const tabs = [
      {
        id: 'sort-by-options',
        content: i18n.translate('AdminHTML.CustomSorting.CustomSorting.title'),
        panelID: 'sort-by-options'
      },
      {
        id: 'default-sorting-options',
        content: i18n.translate('AdminHTML.CustomSorting.DefaultSortingOptions.title'),
        panelID: 'default-sorting-options'
      }
    ];

    const tabComponents = [
      <SortByOptions
        loading={loading}
        sortOptions={sortOptions}
        success={success}
        enabledFeatureBestSelling={enabledFeatureBestSelling}
        enabledFeatureManual={enabledFeatureManual}
        enabledFeatureRelevance={enabledFeatureRelevance}
        sortTypes={sortTypes}
        updatePositionFlag={this.updatePositionFlag}
        updateCustomSortingForm={updateCustomSortingForm}
        defaultSorting={defaultSorting}
      />,
      <DefaultSortingOptions
        loading={loading}
        defaultSorting={defaultSorting}
        sortOptions={sortOptions}
        enabledFeatureBestSelling={enabledFeatureBestSelling}
        enabledFeatureManual={enabledFeatureManual}
        formValues={formValues}
        enabledFeatureRelevance={enabledFeatureRelevance}
        updateCustomSortingForm={updateCustomSortingForm}
      />
    ];

    return (
      <div className="BC-Page BC-Page__CustomSorting">
        <MainMenu menuId="menu-settings-sorting" menuIdMetaMatchOldNav="menu-general-settings___" />
        <Page
          title={i18n.translate('AdminHTML.CustomSorting.CustomSorting.title')}
          primaryAction={
            <div className="BC-Page BC-Page__Button">
              <Stack>
                {!pristine && (
                  <Button disabled={submitting} onClick={this.discardChange}>
                    {i18n.translate('AdminHTML.Common.Action.discard')}
                  </Button>
                )}
                <Button
                  primary
                  onClick={handleSubmit(this.handleSave.bind(this))}
                  disabled={pristine || submitting}
                  loading={saving && !pristine}>
                  {i18n.translate('AdminHTML.Common.Action.saveSettings')}
                </Button>
              </Stack>
            </div>
          }>
          <TitleHelp i18n={i18n} hrefUserManual={URL['Link_V2_Tool_SortByOption_ManualGuide']} />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Installation reminder status banner */}
                <InstallationRemindStatusBanner />

                <ThemeUpgradeFlow2Notification featureID={FEATURE_IDS.SORT_BY_OPTIONS} />

                {/* Did you know? */}
                <DidYouKnow
                  tenantApp={tenantApp}
                  sendRequest={() => this.toggleModal('activeRequestModal')}
                />

                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />

                {/* Processing update banner */}
                {showScheduleSync ? (
                  <ProcessingUpdateBanner syncDuration={syncDuration} featureName="sortByOptions" />
                ) : (
                  ''
                )}
              </div>
            </Layout.Section>

            {/* List of customSorting */}
            <Layout.Section>
              <Card>
                <div className="tabs">
                  <Tabs tabs={tabs} selected={selected} onSelect={this.handleTabChange}>
                    <Card.Section>{tabComponents[selected]}</Card.Section>
                  </Tabs>
                </div>
              </Card>
            </Layout.Section>
          </Layout>
          <PageActions
            primaryAction={{
              content: i18n.translate('AdminHTML.Common.Action.saveSettings'),
              onAction: handleSubmit(this.handleSave.bind(this)),
              disabled: pristine || submitting,
              loading: saving && !pristine
            }}
            secondaryActions={
              !pristine
                ? [
                    {
                      content: i18n.translate('AdminHTML.Common.Action.discard'),
                      onAction: this.discardChange,
                      disabled: submitting
                    }
                  ]
                : []
            }
          />
        </Page>

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={dirty}
        />

        <SendRequestFeatureModal
          active={activeRequestModal}
          handleClose={() => this.toggleModal('activeRequestModal')}
          handleOpenResultModal={() => this.toggleModal('activeModalResult')}
          featureKey="sortByOptions"
        />
        <RequestResultModal
          active={activeModalResult}
          handleClose={() => this.toggleModal('activeModalResult')}
          featureName={i18n.translate(
            'AdminHTML.Common.RequestResultModal.content_t1_sortByOptions'
          )}
        />
      </div>
    );
  }

  // Handle change tab
  handleTabChange = (selectedTabIndex: number) => {
    this.setState({ selected: selectedTabIndex });
  };

  // Handle click save button
  handleSave = async data => {
    const { updateCustomSorting, markDoneStep } = this.props;
    const { positionFlag } = this.state;

    const saveData = buildCustomSortingValues(data, positionFlag);
    markDoneStep('V2_FILTER4_ADVANCED', {
      isDone: true
    });
    this.updatePositionFlag(false);
    await updateCustomSorting(saveData);
  };

  // Handle discard change
  discardChange = () => {
    const { resetForm } = this.props;

    resetForm();
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  updatePositionFlag = value => {
    this.setState({ positionFlag: value });
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const CustomSortingForm = reduxForm({
  form: 'CustomSortingForm', // a unique identifier for this form
  enableReinitialize: true
})(CustomSorting);

const mapDispatchToProps = (dispatch, props) => ({
  // Get available collections
  getFilterData: () => dispatch(FilterDataActions.getFilterData(props.i18n)),
  getAvailableSortTypes: () => dispatch(CustomSortingActions.getAvailableSortTypes(props.i18n)),
  getCustomSorting: () => dispatch(CustomSortingActions.getCustomSorting(props.i18n)),
  updateCustomSorting: customSorting =>
    dispatch(CustomSortingActions.updateCustomSorting(customSorting, props.i18n)),
  updateCustomSortingForm: (index, data) => {
    dispatch(change(`CustomSortingForm`, index, data));
  },
  resetForm: () => dispatch(reset('CustomSortingForm')),
  markDoneStep: (stepName, value) =>
    dispatch(quickStartGuideAction.markDoneStep(stepName, value, props.i18n))
});

const mapStateToProps = (state, props) => {
  const sortByOptions = _.get(state.customSorting, 'sortByOptions', []);
  const collections = _.get(state.filterData.filterData, 'collections', []);
  const formValues = getFormValues('CustomSortingForm')(state);
  const initialValues = buildInitialCustomSortingValues(sortByOptions, collections);
  const sortOptions = _.get(formValues, 'sortOptions', []);
  const defaultSorting = _.get(formValues, 'defaultSorting', {});

  return {
    formValues,
    sortOptions,
    tenantApp: _.get(state, 'oauth.tenantApp', {}),
    defaultSorting,
    getSortTypesLoading: _.get(state.customSorting, 'getSortTypesLoading', false),
    loading: _.get(state.customSorting, 'loading', false),
    saving: _.get(state.customSorting, 'saving', false),
    success: _.get(state.customSorting, 'success', false),
    scheduleSyncIn: _.get(state.customSorting, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(state.customSorting, 'waitSyncAt', 0),
    initialValues,
    enabledFeatureBestSelling: _.get(
      state.oauth.tenantApp,
      'settings.sortByOptions.settings.sort-by-best-selling',
      true
    ),
    enabledFeatureManual: _.get(
      state.oauth.tenantApp,
      'settings.sortByOptions.settings.sort-by-manual',
      true
    ),
    enabledFeatureRelevance: _.get(
      state.oauth.tenantApp,
      'settings.sortByOptions.settings.sort-by-relevance',
      true
    ),
    sortTypes: _.get(state.customSorting, 'sortTypes', [])
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(CustomSortingForm)),
  'MapProduct'
);
